import moment, {Moment} from 'moment';
import React, {useEffect, useRef, useState} from 'react';
import ClipboardButton from 'react-clipboard.js';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {Button} from '@/web-components';
import 'whatwg-fetch'; //IE 11 fetch polyfill
import {LabelDropdown} from '../../components/dropdowns/LabelDropdown/LabelDropdown';
import InputField from '../../components/inputs/input_field';
import SelectInput from '../../components/inputs/select_input';
import Warning from '../../components/warning';
import {BUTTON_COLOR, BUTTON_STYLE, DATE_PICKER_STYLE, INITIALS_SIZE, MODULE_TYPES} from '../../constants';
import DirectApi, {profilePicSrc} from '../../directApi';
import DatePicker from '../../forecast-app/shared/components/date-picker/date_picker_v3';
import Dropdown from '../../forecast-app/shared/components/dropdowns/dropdown';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {HoursInput} from '../../forecast-app/shared/components/inputs/hours-input/hours_input_view';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import Person from '../../forecast-app/shared/components/person/person';
import PersonInitials from '../../forecast-app/shared/components/person/person_initials';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {hasModule} from '../../forecast-app/shared/util/ModuleUtil';
import {
	getProfileName,
	getSelectableProfiles,
	hasPermission,
	personIsAdmin,
	personIsVirtualUser,
} from '../../forecast-app/shared/util/PermissionsUtil';
import {Connection} from '../../forecast-app/shared/util/type-util/Connection';
import Util from '../../forecast-app/shared/util/util';
import HarvestIcon from '../../images/integrations/harvest-logo.png';
import DeleteSisenseUserMutation from '../../mutations/DeleteSisenseUserMutation';
import SetBambooHRUserMutation from '../../mutations/SetBambooHRUserMutation';
import CreateSlackUserVerificationKeyMutation from '../../mutations/slack_integration/CreateSlackUserVerificationKeyMutation';
import SetSlackUserMutation from '../../mutations/slack_integration/SetSlackUserMutation';
import UpdatePersonMutation from '../../mutations/update_person_mutation.modern';
import UpdateViewerMutation from '../../mutations/update_viewer_mutation.modern';
import {PERMISSION_TYPE} from '../../Permissions';
import {versionManager} from '../../version_manager';
import {HiBobIntegration} from './integrations/HiBobIntegration';
import TeamMemberStatusSection from './people/TeamMemberStatusSection';
import SettingsCalendarWrapper from './settingsCalendar/SettingsCalendarWrapper';
import SettingsProfileSubNav from './SettingsProfileSubNav';
import ProfileSkills from './skills/ProfileSkills';
import {type Company, type Person as PersonType, type Profile} from './types';
import {UnlinkButton} from './UnlinkButton';

type GithubUser = {
	id: unknown;
	username: string;
	avatar_url: string | undefined;
};

type GitLabUser = {
	name: string;
	avatar_url: string | undefined;
	username: string;
};

type SageIntacctUser = {
	id?: unknown;
	sageIntacctId: unknown;
	sageIntacctName: unknown;
	sageIntacctEmail: unknown;
	sageIntacctLocationId: unknown;
};

type Unit4User = {
	surname: string;
	firstName: string;
	personId: string;
};

type DropdownOption = {
	label: string;
	value: unknown;
	linked: unknown;
};

type JiraUser = {
	id?: unknown;
} & (
	| {
			jiraCloudId: unknown;
	  }
	| {
			jiraServerId: unknown;
	  }
);

function getWorkingHours(language: string, person: PersonType): number[] {
	return language === 'ENGLISH_US'
		? [
				person.sunday / 60,
				person.monday / 60,
				person.tuesday / 60,
				person.wednesday / 60,
				person.thursday / 60,
				person.friday / 60,
				person.saturday / 60,
		  ]
		: [
				person.monday / 60,
				person.tuesday / 60,
				person.wednesday / 60,
				person.thursday / 60,
				person.friday / 60,
				person.saturday / 60,
				person.sunday / 60,
		  ];
}

const TopHeader = ({viewUser, person}: {viewUser: boolean; person: PersonType}): JSX.Element => {
	const {formatMessage} = useIntl();
	const history = useHistory();

	return (
		<TopHeaderBarWrapper>
			<TopHeaderBar
				title={
					viewUser
						? person.client
							? formatMessage({id: 'settings_profile.client-user-profile'})
							: formatMessage({id: 'settings_profile.team-member-profile'})
						: 'Account Configuration'
				}
				content={viewUser ? [{type: TopHeaderBar.TYPE.BACK_BUTTON, history}] : []}
			/>
		</TopHeaderBarWrapper>
	);
};

type SettingsProfileProps = {
	person: PersonType;
	viewer: {
		language: unknown;
		company: {modules: unknown; profiles: Connection<Profile>};
	};
	company: Company;
	language: string;
	oauthSlack: unknown;
	showEmailValidationError: boolean;
	emailValidationErrorMessage: unknown;

	viewUser: boolean;
	changeName: (...args: Array<unknown>) => unknown;
	changeEmail: (...args: Array<unknown>) => unknown;
	changePhone: (...args: Array<unknown>) => unknown;
	changeProfile: (...args: Array<unknown>) => unknown;
	changeDepartment: (...args: Array<unknown>) => unknown;
	changeJobTitle: (...args: Array<unknown>) => unknown;
	changeHolidayCalendar: (...args: Array<unknown>) => unknown;
	changeRole: (...args: Array<unknown>) => unknown;
	changeLanguage: (...args: Array<unknown>) => unknown;
	changeProfilePicture: (...args: Array<unknown>) => unknown;
	deleteProfilePicture: (...args: Array<unknown>) => unknown;
	isUploading: boolean;
	isViewer: boolean;
	isProfileUpdating: boolean;
	showBillingInfo: boolean;
};

export const SettingsProfile: React.FC<SettingsProfileProps> = ({
	person,
	viewer,
	company,
	isProfileUpdating,
	language,
	viewUser,
	changeName,
	changeHolidayCalendar,
	changeJobTitle,
	changeEmail,
	changePhone,
	changeProfile,
	changeRole,
	changeDepartment,
	changeLanguage,
	changeProfilePicture,
	deleteProfilePicture,
	isViewer,
	...props
}) => {
	useEffect(() => {
		document.title = 'Details - My Profile - Forecast';
	}, []);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const {formatMessage} = useIntl();

	const [name, setName] = useState([person.firstName, person.lastName].filter(Boolean).join(' '));
	const [phone, setPhone] = useState(person.phone ?? '');
	const [jobTitle, setJobTitle] = useState(person.jobTitle ?? '');
	const [selectedLabels, setSelectedLabels] = useState(
		person.personLabels.edges.map(personLabel => personLabel.node.label.id)
	);

	const [email, setEmail] = useState(person.email ?? '');
	useEffect(() => {
		setEmail(person.email);
	}, [person.email]);

	const [workingHours, setWorkingHours] = useState(getWorkingHours(language, person));
	useEffect(() => {
		setWorkingHours(getWorkingHours(language, person));
	}, [language, person]);

	const [showEmailValidationError, setShowEmailValidationError] = useState(false);
	useEffect(() => {
		setShowEmailValidationError(props.showEmailValidationError);
	}, [props.showEmailValidationError, props.emailValidationErrorMessage]);

	const [gitlabLoading, setGitlabLoading] = useState(false);
	const [gitlabServerLoading, setGitlabServerLoading] = useState(false);
	const [gitlabUsers, setGitlabUsers] = useState<GitLabUser[]>([]);
	const [gitlabServerUsers, setGitlabServerUsers] = useState<GitLabUser[]>([]);
	const [gitlabSearchString, setGitlabSearchString] = useState('');
	const [gitlabServerSearchString, setGitlabServerSearchString] = useState('');
	const [linkedGitlabUser, setLinkedGitlabUser] = useState<GitLabUser | null>(null);
	const [linkedGitlabServerUser, setLinkedGitlabServerUser] = useState<GitLabUser | null>(null);

	const [githubLoading, setGithubLoading] = useState(false);
	const [githubUsers, setGithubUsers] = useState<GithubUser[]>([]);
	const [githubSearchString, setGithubSearchString] = useState('');
	const [linkedGithubUser, setLinkedGithubUser] = useState<GithubUser | null>(null);

	const [harvestLoading, setHarvestLoading] = useState(false);
	const [harvestUsersName, setHarvestUsersName] = useState('');

	const [unit4Loading, setUnit4Loading] = useState(false);
	const [unit4Users, setUnit4Users] = useState<Unit4User[]>([]);
	const [unit4UserOptions, setUnit4UserOptions] = useState([]);

	const [bambooHRUsers, setBambooHRUsers] = useState<DropdownOption[]>([]);
	const [bambooHRLoading, setBambooHRLoading] = useState(false);

	const [slackUsers, setSlackUsers] = useState<DropdownOption[]>([]);
	const [slackLoading, setSlackLoading] = useState(false);

	const [jiraCloudLoading, setJiraCloudLoading] = useState(false);
	const [jiraServerLoading, setJiraServerLoading] = useState(false);
	const [jiraCloudUsers, setJiraCloudUsers] = useState<DropdownOption[]>([]);
	const [jiraServerUsers, setJiraServerUsers] = useState<DropdownOption[]>([]);
	const jiraCloudUsersDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [sageIntacctUsers, setSageIntacctUsers] = useState([]);
	const [sageIntacctLoading, setSageIntacctLoading] = useState(false);

	const showChangesSavedToast = () => {
		createToast({duration: 2000, message: formatMessage({id: 'common.changes_saved'})});
	};

	const handleBlurName = () => {
		const [first, ...rest] = name.trim().split(' ');
		changeName(first.trim(), rest.join(' ').trim());
	};

	const handleChangeProfilePicture = () => {
		const fileInput = fileInputRef.current;
		if (
			fileInput?.files &&
			fileInput.files[0] !== undefined &&
			fileInput.files[0] !== null &&
			(fileInput.files[0].type === 'image/jpeg' ||
				fileInput.files[0].type === 'image/gif' ||
				fileInput.files[0].type === 'image/png')
		) {
			changeProfilePicture(fileInput.files[0]);
		} else {
			showModal({
				type: MODAL_TYPE.GENERIC,
				content: (
					<div className="default-warning-modal">
						<Warning messageId="warning_modal.profile_picture_wrong_format_title" />
						<div className="warning-part-2">
							{formatMessage({id: 'warning_modal.profile_picture_wrong_format_info'})}
						</div>
					</div>
				),
				buttons: [
					{
						text: formatMessage({id: 'common.cancel'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
				],
			});
		}
	};

	const updateLabels = updatedLabels => {
		setSelectedLabels(updatedLabels);
		if (isViewer) {
			Util.CommitMutation(
				UpdateViewerMutation,
				{
					labels: updatedLabels,
				},
				() => showChangesSavedToast()
			);
		} else {
			Util.CommitMutation(
				UpdatePersonMutation,
				{
					id: person.id,
					labels: updatedLabels,
				},
				() => showChangesSavedToast()
			);
		}
	};

	const saveWorkingHours = (i, e) => {
		const newHours = workingHours.slice();
		if (newHours[i] === e) return; //Don't trigger if the value did not change
		newHours[i] = e;

		const validateHours = val => {
			if (isNaN(val)) return false;
			if (parseFloat(val) < -600000) return false;
			else if (parseFloat(val) > 600000) return false;
			return true;
		};

		let monday, tuesday, wednesday, thursday, friday, saturday, sunday;
		if (viewer.language === 'ENGLISH_US') {
			sunday = newHours[0];
			monday = newHours[1];
			tuesday = newHours[2];
			wednesday = newHours[3];
			thursday = newHours[4];
			friday = newHours[5];
			saturday = newHours[6];
		} else {
			monday = newHours[0];
			tuesday = newHours[1];
			wednesday = newHours[2];
			thursday = newHours[3];
			friday = newHours[4];
			saturday = newHours[5];
			sunday = newHours[6];
		}
		let isValid = true;
		[monday, tuesday, wednesday, thursday, friday, saturday, sunday].forEach(val => {
			if (!validateHours(val)) {
				isValid = false;
			}
		});

		const working_hours = [monday, tuesday, wednesday, thursday, friday, saturday];
		if (language === 'ENGLISH_US') {
			working_hours.splice(0, 0, sunday);
		} else {
			working_hours.push(sunday);
		}
		setWorkingHours(working_hours);
		if (!isValid) return;
		if (viewUser) {
			Util.CommitMutation(
				UpdatePersonMutation,
				{
					id: person.id,
					monday: Math.round(monday * 60),
					tuesday: Math.round(tuesday * 60),
					wednesday: Math.round(wednesday * 60),
					thursday: Math.round(thursday * 60),
					friday: Math.round(friday * 60),
					saturday: Math.round(saturday * 60),
					sunday: Math.round(sunday * 60),
				},
				() => showChangesSavedToast()
			);
		} else {
			Util.CommitMutation(
				UpdateViewerMutation,
				{
					monday: Math.round(monday * 60),
					tuesday: Math.round(tuesday * 60),
					wednesday: Math.round(wednesday * 60),
					thursday: Math.round(thursday * 60),
					friday: Math.round(friday * 60),
					saturday: Math.round(saturday * 60),
					sunday: Math.round(sunday * 60),
				},
				() => showChangesSavedToast()
			);
		}
		setWorkingHours(newHours);
	};

	const fetchGitLabUsers = server => {
		setGitlabLoading(true);
		DirectApi.Fetch(`gitlab_users?server=${server}&searchString=${server ? gitlabServerSearchString : gitlabSearchString}`)
			.then(json => {
				if (server) {
					setGitlabServerLoading(false);
					setGitlabServerUsers(json || []);
				} else {
					setGitlabLoading(false);
					setGitlabUsers(json || []);
				}
			})
			.catch(() => {
				setGitlabServerLoading(false);
				setGitlabLoading(false);
			});
	};

	const fetchUnit4Resources = filterString => {
		if (company.unit4Enabled) {
			if (!unit4Loading) {
				setUnit4Loading(true);

				DirectApi.Fetch(`unit4_users?filterString=${filterString}`).then(users => {
					const options = users
						.filter(u => !u.forecastId)
						.sort((a, b) => {
							const userA = a.firstName.toUpperCase().trim() + ' ' + a.surname.toUpperCase().trim();
							const userB = b.firstName.toUpperCase().trim() + ' ' + b.surname.toUpperCase().trim();
							if (userA > userB) {
								return 1;
							} else if (userA < userB) {
								return -1;
							}
							return 0;
						})
						.map(u => ({value: u.personId, label: u.firstName.trim() + ' ' + u.surname.trim()}));
					setUnit4Users(users);
					setUnit4UserOptions(options);
					setUnit4Loading(false);
				});
			}
		}
	};

	const fetchBambooHRUsers = () => {
		if (company.bambooHREnabled) {
			setBambooHRLoading(true);

			const personId = person.actualPersonId ?? person.id;

			DirectApi.Fetch(`bamboohr/employees?personId=${Util.getIdFromBase64String(personId)}`).then(users => {
				setBambooHRUsers(
					users
						.map(u => ({value: u.id, label: `${u.firstName.trim()} ${u.lastName.trim()} (${u.workEmail})`}))
						.sort((u1, u2) => (u1.label < u2.label ? -1 : 1))
				);
				setBambooHRLoading(false);
			});
		}
	};

	const fetchSlackUsers = () => {
		if (company.newSlackEnabled && person.slackId) {
			setSlackLoading(true);

			const personId = person.actualPersonId ?? person.id;

			DirectApi.Fetch(`slack/users/${Util.getIdFromBase64String(personId)}`).then(users => {
				setSlackUsers(
					users
						.map(u => ({
							value: u.id,
							label: `${u.profile.first_name.trim()} ${u.profile.last_name.trim()}`,
						}))
						.sort((u1, u2) => (u1.label < u2.label ? -1 : 1))
				);
				setSlackLoading(false);
			});
		}
	};

	const fetchSageIntacctUsers = () => {
		if (company.sageIntacctConnectionEnabled && !person.sageIntacctId) {
			setSageIntacctLoading(true);
			DirectApi.Fetch('sage_intacct/employees').then(res => {
				if (res?.employees) {
					setSageIntacctUsers(
						res.employees
							.map(u => ({
								value: u.EMPLOYEEID,
								label: `${u.PERSONALINFO__2e__FIRSTNAME} ${u.PERSONALINFO__2e__LASTNAME} (${u.PERSONALINFO__2e__EMAIL1})`,
								name: `${u.PERSONALINFO__2e__FIRSTNAME} ${u.PERSONALINFO__2e__LASTNAME}`,
								email: u.PERSONALINFO__2e__EMAIL1,
								location: u.LOCATIONID,
							}))
							.sort((a, b) => (a.label.toUpperCase().trim() > b.label.toUpperCase().trim() ? 1 : -1))
					);
					setSageIntacctLoading(false);
				} else {
					setSageIntacctUsers([]);
					setSageIntacctLoading(false);
				}
			});
		}
	};

	const fetchHarvestUser = () => {
		if (company.harvestEnabled && person.harvestUser) {
			setHarvestLoading(true);

			let param;
			if (isViewer) {
				param = person.actualPersonId;
			} else {
				param = person.id;
			}

			DirectApi.Fetch(`harvest_users?personId=${param}`).then(json => {
				setHarvestUsersName(json.user);
				setHarvestLoading(false);
			});
		} else if (company.harvestEnabled) {
			setHarvestUsersName('User is not linked to a Harvest user');
		}
	};

	const fetchJiraCloudUsers = (searchString: string, immediate = false) => {
		if (!company.jiraCloudEnabled) {
			return;
		}

		const clearDebounceTimer = () => {
			if (jiraCloudUsersDebounceTimerRef.current !== null) {
				clearTimeout(jiraCloudUsersDebounceTimerRef.current);
				jiraCloudUsersDebounceTimerRef.current = null;
			}
		};

		if (searchString.length <= 3) {
			clearDebounceTimer();
			return;
		}

		const runFetch = () => {
			setJiraCloudLoading(true);

			DirectApi.Fetch(`jira/cloud/users/active?searchString=${searchString}`)
				.then(json => {
					setJiraCloudLoading(false);
					setJiraCloudUsers(
						json.map(user => ({
							linked: user.linked,
							value: user.accountId,
							label: `${user.displayName} (${user.emailAddress ? user.emailAddress : 'No Email'})`,
						})) || []
					);
				})
				.catch(() => {
					setJiraCloudLoading(false);
					setJiraCloudUsers([]);
				});
		};

		if (immediate) {
			clearDebounceTimer();
			runFetch();
			return;
		}

		clearDebounceTimer();
		jiraCloudUsersDebounceTimerRef.current = setTimeout(() => {
			jiraCloudUsersDebounceTimerRef.current = null;
			runFetch();
		}, 350);
	};

	const fetchJiraServerUsers = () => {
		if (company.jiraServerEnabled) {
			setJiraServerLoading(true);

			DirectApi.Fetch(`jira/server/users/active`)
				.then(json => {
					setJiraServerLoading(false);
					setJiraServerUsers(
						json.map(user => ({
							linked: user.linked,
							value: user.key,
							label: `${user.displayName} (${user.emailAddress ? user.emailAddress : 'No Email'})`,
						})) || []
					);
				})
				.catch(() => {
					setJiraServerLoading(false);
					setJiraServerUsers([]);
				});
		}
	};

	const fetchGitLabUser = () => {
		if (company.gitlabEnabled && person.gitlabUser) {
			const id = person.gitlabUser.integrationUserId;
			if (id) {
				DirectApi.Fetch(`gitlab_users?server=false&id=${id}`).then(json => {
					setLinkedGitlabUser(json);
				});
			}
		}
		if (company.gitlabServerEnabled && person.gitlabServerUser) {
			const id = person.gitlabServerUser.integrationUserId;
			if (id) {
				DirectApi.Fetch(`gitlab_users?server=true&id=${id}`).then(json => {
					setLinkedGitlabServerUser(json);
				});
			}
		}
	};

	const updateGitlabUser = (userId, server, onSuccess) => {
		if (viewUser) {
			const object = {
				id: person.id,
				gitlabPersonId: server ? undefined : userId,
				gitlabServerPersonId: server ? userId : undefined,
			};
			Util.CommitMutation(UpdatePersonMutation, object, onSuccess);
		} else {
			Util.CommitMutation(
				UpdateViewerMutation,
				{
					gitlabPersonId: server ? undefined : userId,
					gitlabServerPersonId: server ? userId : undefined,
				},
				onSuccess
			);
		}
	};

	const selectGitlabUser = (u, server) => {
		const imageSrc = profilePicSrc(person.profilePictureId);
		const onSuccess = () => {
			if (server) {
				setLinkedGitlabServerUser(u);
			} else {
				setLinkedGitlabUser(u);
			}
		};
		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings_profile.link_users',
			warningInformation: [
				<div className="gitlab-modal-content">
					<div>
						<FormattedMessage id="settings_profile.integration_user_link" />
					</div>
					<Person name={name} showName={true} role={email} showRole={true} imageSrc={imageSrc} imageSize="medium" />
					<div>
						<FormattedMessage id="settings_profile.gitlab_link2" />
					</div>
					<div className="gitlab-user">
						<img height="24" width="24" src={u.avatar_url} />
						<div className="name">
							<span>{u.name}</span>
							<span className="username">{'@' + u.username}</span>
						</div>
					</div>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.link'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => updateGitlabUser(u.id, server, onSuccess),
				},
			],
		});
	};

	const removeGitLabUser = server => {
		const onSuccess = () => {
			if (server) {
				setLinkedGitlabServerUser(null);
			} else {
				setLinkedGitlabUser(null);
			}
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings_profile.gitlab_unlink',
			warningInformation: [formatMessage({id: 'settings_profile.gitlab_unlink_modal_description'})],
			useInfoIcon: true,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => updateGitlabUser(0, server, onSuccess),
				},
			],
		});
	};

	const fetchGithubUsers = () => {
		if (company.githubEnabled) {
			setGithubLoading(true);

			DirectApi.Fetch(`github_users?searchString=${githubSearchString}`)
				.then(json => {
					setGithubLoading(false);
					setGithubUsers(json || []);
				})
				.catch(() => {
					setGithubLoading(false);
					setGithubUsers([]);
				});
		}
	};

	const fetchGithubUser = () => {
		if (company.githubEnabled && person.githubUser) {
			const id = person.githubUser.integrationUserId;
			if (id) {
				DirectApi.Fetch(`github_users?&id=${id}`)
					.then(json => {
						setLinkedGithubUser(json);
					})
					.catch(() => {
						setLinkedGithubUser(null);
					});
			}
		}
	};

	const updateGithubUser = (userId, onSuccess) => {
		if (viewUser) {
			const object = {
				id: person.id,
				githubPersonId: userId,
			};
			Util.CommitMutation(UpdatePersonMutation, object, onSuccess);
		} else {
			Util.CommitMutation(
				UpdateViewerMutation,
				{
					githubPersonId: userId,
				},
				onSuccess
			);
		}
	};

	const selectGithubUser = (u: GithubUser) => {
		const imageSrc = profilePicSrc(person.profilePictureId);
		const onSuccess = () => {
			setLinkedGithubUser(u);
		};
		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings_profile.link_users',
			warningInformation: [
				<div className="github-modal-content">
					<div>
						<FormattedMessage id="settings_profile.integration_user_link" />
					</div>
					<Person name={name} showName={true} role={email} showRole={true} imageSrc={imageSrc} imageSize="medium" />
					<div>
						<FormattedMessage id="settings_profile.github_link2" />
					</div>
					<div className="github-user">
						<img height="24" width="24" src={u.avatar_url} alt={''} />
						<div className="name">
							<span>{u.username}</span>
							<span className="username">{'@' + u.username}</span>
						</div>
					</div>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.link'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => updateGithubUser(u.id, onSuccess),
				},
			],
		});
	};

	const removeGithubUser = () => {
		const onSuccess = () => {
			setLinkedGithubUser(null);
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings_profile.github_unlink',
			warningInformation: [formatMessage({id: 'settings_profile.github_unlink_modal_description'})],
			useInfoIcon: true,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => updateGithubUser(0, onSuccess),
				},
			],
		});
	};

	const unlinkUnit4User = () => {
		showModal({
			type: MODAL_TYPE.GENERIC,
			className: 'default-warning-modal',
			content: <Warning messageId="settings_profile.unit4_unlink" />,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						if (viewUser) {
							Util.CommitMutation(UpdatePersonMutation, {
								id: person.id,
								unit4PersonId: null,
							});
						} else {
							Util.CommitMutation(UpdateViewerMutation, {
								unit4PersonId: null,
							});
						}
					},
				},
			],
		});
	};

	const linkUnit4User = option => {
		const imageSrc = profilePicSrc(person.profilePictureId);
		const callback = () => {
			if (option) {
				if (viewUser) {
					Util.CommitMutation(UpdatePersonMutation, {
						id: person.id,
						unit4PersonId: option.value,
					});
				} else {
					Util.CommitMutation(UpdateViewerMutation, {
						unit4PersonId: option.value,
					});
				}
			}
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings_profile.link_users',
			warningInformation: [
				<div className="unit4-modal-content">
					<div>
						<FormattedMessage id="settings_profile.integration_user_link" />
					</div>
					<Person name={name} showName={true} role={email} showRole={true} imageSrc={imageSrc} imageSize="medium" />
					<div>
						<FormattedMessage id="settings_profile.unit4_link" />
					</div>
					<div className="unit4-user">{option.label}</div>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: <FormattedMessage id="common.cancel" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: <FormattedMessage id="common.link" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: callback,
				},
			],
		});
	};

	const setBambooHRUser = (input?: {value: unknown}) => {
		const personId = person.actualPersonId ?? person.id;

		if (input) {
			// Linking
			Util.CommitMutation(SetBambooHRUserMutation, {forecastPersonId: personId, bambooHRUserId: input.value});
		} else {
			// Unlinking

			showModal({
				type: MODAL_TYPE.GENERIC,
				className: 'default-warning-modal',
				content: <Warning messageId="settings_profile.bamboohr_unlink" />,
				buttons: [
					{
						text: formatMessage({id: 'common.cancel'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
					{
						text: formatMessage({id: 'common.unlink'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.RED,
						callback: () =>
							Util.CommitMutation(SetBambooHRUserMutation, {forecastPersonId: personId, bambooHRUserId: null}),
					},
				],
			});
		}
	};

	const setSageIntacctUser = (input?: {value: unknown; name: unknown; email: unknown; location: unknown}) => {
		const data: SageIntacctUser = {
			sageIntacctId: input?.value ? input?.value : null,
			sageIntacctName: input?.name ? input?.name : null,
			sageIntacctEmail: input?.email ? input?.email : null,
			sageIntacctLocationId: input?.location ? input?.location : null,
		};
		if (viewUser) {
			data.id = person.id;
		}
		const mutation = viewUser ? UpdatePersonMutation : UpdateViewerMutation;

		if (input) {
			Util.CommitMutation(mutation, data, null, true);
		} else {
			showModal({
				type: MODAL_TYPE.GENERIC,
				className: 'default-warning-modal',
				content: <Warning message="Are you sure you want to unlink this user from Sage Intacct?" />,
				buttons: [
					{
						text: formatMessage({id: 'common.cancel'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
					{
						text: formatMessage({id: 'common.unlink'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.RED,
						callback: () => Util.CommitMutation(mutation, data, null, true),
					},
				],
			});
		}
	};

	const unlinkSlackUser = () => {
		const personId = person.actualPersonId ?? person.id;
		// Unlinking

		showModal({
			type: MODAL_TYPE.GENERIC,
			className: 'default-warning-modal',
			content: <Warning messageId="settings_profile.slack_unlink" />,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => Util.CommitMutation(SetSlackUserMutation, {personId: personId, slackId: null}),
				},
			],
		});
	};

	const linkSlackUser = () => {
		const redirectUri = `${process.env.API_URL}/slack/oauth_user`;
		const onSuccess = ({createSlackUserVerificationKey}) => {
			const scopes = ['channels:read', 'groups:read'];
			window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${
				process.env.SLACK_APP_CLIENT_ID
			}&redirect_uri=${redirectUri}&scope=&user_scope=${scopes.join(',')}&state=${createSlackUserVerificationKey.key}`;
		};

		Util.CommitMutation(CreateSlackUserVerificationKeyMutation, null, onSuccess);
	};

	const unlinkJiraUser = type => {
		showModal({
			type: MODAL_TYPE.GENERIC,
			className: 'default-warning-modal',
			content: <Warning messageId="settings_profile.jira_unlink" />,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						const data: JiraUser = type === 'cloud' ? {jiraCloudId: null} : {jiraServerId: null};
						if (viewUser) {
							data.id = person.id;
							Util.CommitMutation(UpdatePersonMutation, data);
						} else {
							Util.CommitMutation(UpdateViewerMutation, data);
						}
					},
				},
			],
		});
	};

	const linkJiraUser = (option, type) => {
		const imageSrc = profilePicSrc(person.profilePictureId);
		const callback = () => {
			if (option) {
				const data: JiraUser = type === 'cloud' ? {jiraCloudId: option.value} : {jiraServerId: option.value};
				if (viewUser) {
					data.id = person.id;
					Util.CommitMutation(UpdatePersonMutation, data);
				} else {
					Util.CommitMutation(UpdateViewerMutation, data);
				}
			}
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings_profile.link_users',
			warningInformation: [
				<div className="jira-modal-content">
					<div>
						<FormattedMessage id="settings_profile.integration_user_link" />
					</div>
					<Person name={name} showName={true} role={email} showRole={true} imageSrc={imageSrc} imageSize="medium" />
					<div>
						<FormattedMessage id="settings_profile.jira_link" />
					</div>
					<div className="jira-user">{option.label}</div>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: <FormattedMessage id="common.cancel" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: <FormattedMessage id="common.link" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: callback,
				},
			],
		});
	};

	const updateStartDate = (startDate: Moment | null) => {
		const onSuccess = () => {
			showChangesSavedToast();
		};
		const startDateValue = startDate && startDate.format('YYYY-MM-DD');

		if (isViewer) {
			Util.CommitMutation(
				UpdateViewerMutation,
				{
					startDate: startDateValue,
				},
				onSuccess
			);
		} else {
			Util.CommitMutation(
				UpdatePersonMutation,
				{
					id: person.id,
					startDate: startDateValue,
				},
				onSuccess
			);
		}
	};

	const updateEndDate = (endDate: Moment | null) => {
		const onSuccess = () => {
			showChangesSavedToast();
		};
		const endDateValue = endDate && endDate.format('YYYY-MM-DD');

		if (isViewer) {
			Util.CommitMutation(
				UpdateViewerMutation,
				{
					endDate: endDateValue,
				},
				onSuccess
			);
		} else {
			Util.CommitMutation(
				UpdatePersonMutation,
				{
					id: person.id,
					endDate: endDateValue,
				},
				onSuccess
			);
		}
	};

	const deleteSisenseUser = () => {
		const callbackPositive = () => {
			const onSuccess = res => {
				const {errors} = res.deleteSisenseUser;
				if (errors?.length) {
					if (errors[0] === 'PERSON_OWNS_DASHBOARDS') {
						showModal({
							type: MODAL_TYPE.WARNING,
							warningMessageId: 'sisense.person_owns_dashboards.title',
							warningInformation: [<FormattedMessage id="sisense.person_owns_dashboards.message" />],
							useInfoIcon: true,
							buttons: [
								{
									text: formatMessage({id: 'common.ok'}),
									style: BUTTON_STYLE.FILLED,
									color: BUTTON_COLOR.WHITE,
								},
							],
						});
					} else {
						createToast({duration: 2000, message: 'Error deleting Advanced Analytics user'});
					}
				} else {
					createToast({duration: 2000, message: 'Advanced Analytics user deleted successfully'});
				}
			};

			Util.CommitMutation(
				DeleteSisenseUserMutation,
				{
					personId: person.actualPersonId ?? person.id,
				},
				onSuccess
			);
		};
		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'warning-modal.edit-warning-title',
			warningInformation: [<FormattedMessage id="sisense.delete_user.message" />],
			useInfoIcon: true,
			buttons: [
				{
					text: formatMessage({id: 'common.delete'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: callbackPositive,
				},
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
			],
		});
	};

	const linkAdoUser = () => {
		showModal({
			type: MODAL_TYPE.ADO_LINK_USER,
			isViewer: isViewer,
			personId: person.id,
		});
	};

	const unlinkAdoUser = () => {
		showModal({
			type: MODAL_TYPE.WARNING,
			message: `Are you sure you want to unlink ${person.firstName}${
				person.lastName ? ` ${person.lastName}` : ''
			} from Azure DevOps?`,
			useInfoIcon: true,
			buttons: [
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						const onSuccess = () => {
							createToast({duration: 2000, message: 'User unlinked from Azure DevOps'});
						};
						if (isViewer) {
							Util.CommitMutation(
								UpdateViewerMutation,
								{
									adoUserId: '',
								},
								onSuccess
							);
						} else {
							Util.CommitMutation(
								UpdatePersonMutation,
								{
									id: person.id,
									adoUserId: '',
								},
								onSuccess
							);
						}
					},
				},
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
			],
		});
	};

	useEffect(() => {
		// If oauth is true "click" the link slack user button
		if (props.oauthSlack) linkSlackUser();

		fetchHarvestUser();
		fetchGitLabUser();
		fetchGithubUser();
		fetchJiraCloudUsers((person.jiraCloudId as string) || '', true);
		fetchJiraServerUsers();
		fetchBambooHRUsers();
		fetchSlackUsers();
		fetchSageIntacctUsers();

		return () => {
			if (jiraCloudUsersDebounceTimerRef.current !== null) {
				clearTimeout(jiraCloudUsersDebounceTimerRef.current);
			}
		};
	}, []);

	const allRoles = company.roles.edges.map(edge => ({value: edge.node.id, label: edge.node.name}));
	const departments = company.departments.edges.map(edge => ({value: edge.node.id, label: edge.node.name}));

	const languages = [
		{value: 'DANISH', label: 'Dansk (DK)'},
		{value: 'ENGLISH_US', label: 'English (US)'},
		{value: 'ENGLISH_UK', label: 'English (UK)'},
		{value: 'ENGLISH_EU', label: 'English (EU)'},
		{value: 'FRENCH', label: 'Français (FR)'},
	];
	if (person.language === 'SPANISH') {
		languages.push({value: 'SPANISH', label: 'Español (ES)'});
	}

	const weekdays =
		language === 'ENGLISH_US'
			? [
					formatMessage({id: 'common.sunday'}),
					formatMessage({id: 'common.monday'}),
					formatMessage({id: 'common.tuesday'}),
					formatMessage({id: 'common.wednesday'}),
					formatMessage({id: 'common.thursday'}),
					formatMessage({id: 'common.friday'}),
					formatMessage({id: 'common.saturday'}),
			  ]
			: [
					formatMessage({id: 'common.monday'}),
					formatMessage({id: 'common.tuesday'}),
					formatMessage({id: 'common.wednesday'}),
					formatMessage({id: 'common.thursday'}),
					formatMessage({id: 'common.friday'}),
					formatMessage({id: 'common.saturday'}),
					formatMessage({id: 'common.sunday'}),
			  ];

	const holidayCalendarOptions = [{value: null as unknown, label: formatMessage({id: 'settings.rate_card_default'})}].concat(
		company.holidayCalendars.edges.map(holidayCalendarEdge => {
			return {value: holidayCalendarEdge.node.id, label: holidayCalendarEdge.node.name};
		})
	);
	const forecast_user = localStorage.getItem('forecast_user');
	const webFrontVersion = versionManager.getClientMajorVersion() + '.' + versionManager.getClientMinorVersion();
	const webFrontVersionTime = moment(versionManager.getClientVersionTime());
	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);

	let unit4Username: string | number = '';
	if (person.unit4UserObject && unit4Users) {
		const unit4User = unit4Users.find(u => parseInt(u.personId) === person.unit4UserObject);
		unit4Username = unit4User
			? unit4User.firstName.trim() + ' ' + unit4User.surname.trim()
			: (person.unit4UserObject as number);
	}

	let jiraCloudUsername = '';
	if (!jiraCloudLoading && jiraCloudUsers) {
		const jiraUser = jiraCloudUsers.find(u => u.value === person.jiraCloudId);
		if (jiraUser) jiraCloudUsername = jiraUser.label;
	}

	let jiraServerUsername = '';
	if (!jiraServerLoading && jiraServerUsers) {
		const jiraUser = jiraServerUsers.find(u => u.value === person.jiraServerId);
		if (jiraUser) jiraServerUsername = jiraUser.label;
	}

	let bambooHRUsername = '';
	if (!bambooHRLoading && bambooHRUsers) {
		const bambooUser = bambooHRUsers.find(u => u.value === person.bambooHRId);
		if (bambooUser) bambooHRUsername = bambooUser.label;
	}

	let slackUsername = '';
	if (!slackLoading && slackUsers) {
		const slackUser = slackUsers.find(u => u.value === person.slackId);
		if (slackUser) slackUsername = slackUser.label;
	}

	const labels = company.labels.edges.map(edge => edge.node);

	return (
		<div className="section-content settings-app settings-profile" data-cy={'profile-page'}>
			{props.children}
			<TopHeader person={person} viewUser={viewUser} />
			{isViewer && <SettingsProfileSubNav viewer={viewer} />}
			<CustomScrollDiv>
				<div className="section-body">
					<div className="inner">
						<fieldset className="personal-info">
							<div className="info-header">
								<h3>
									{viewUser
										? person.client
											? formatMessage({id: 'settings_profile.client_user_profile_info'})
											: formatMessage({id: 'settings_profile.team-member-info'})
										: formatMessage({id: 'settings_profile.personal-info'})}
								</h3>
							</div>

							<p className="description">
								{viewUser ? (
									person.client ? (
										<FormattedMessage id="settings_profile.client_user_info" />
									) : (
										<FormattedMessage id="settings.team_member_info" />
									)
								) : (
									<FormattedMessage id="settings.profile_info" />
								)}
							</p>

							<div className="titles-material">
								<InputField
									cy={'member-name'}
									label={formatMessage({id: 'common.name'})}
									type="text"
									placeholder={
										viewUser
											? person.client
												? formatMessage({id: 'settings_profile.enter_client_user_name'})
												: formatMessage({id: 'settings_profile.enter-team-member-name'})
											: formatMessage({id: 'settings_profile.enter-your-name'})
									}
									value={name}
									onBlur={handleBlurName}
									onChange={setName}
									blurOnEnter={true}
								/>
								{person.client ? (
									<InputField
										label={formatMessage({id: 'common.phone'})}
										type="text"
										placeholder={formatMessage({id: 'project-client.contact-phone-input-placeholder'})}
										value={phone}
										onChange={value => {
											setPhone(value);
										}}
										onBlur={() => {
											changePhone(phone);
										}}
										blurOnEnter={true}
										maxLength={45}
									/>
								) : null}
								<InputField
									cy={'member-email'}
									label={formatMessage({id: 'common.email'})}
									locked={person.demoUser}
									type="email"
									placeholder={
										viewUser
											? person.demoUser
												? formatMessage({id: 'settings_profile.demo_user_no_email'})
												: person.client
												? formatMessage({id: 'settings_profile.enter_client_user_email'})
												: formatMessage({id: 'settings_profile.enter-team-member-email'})
											: formatMessage({id: 'settings_profile.enter-your-email'})
									}
									id="email_address"
									value={email}
									onChange={value => {
										setEmail(value);
									}}
									onBlur={valid => {
										if (valid) {
											changeEmail(email);
										}
									}}
									checkEmailValidity={isValid => {
										setShowEmailValidationError(!isValid);
									}}
									errorMessage={
										props.emailValidationErrorMessage && props.emailValidationErrorMessage !== ''
											? props.emailValidationErrorMessage
											: formatMessage({id: 'settings_profile.invalid_email'})
									}
									inputValid={!showEmailValidationError}
									blurOnEnter={true}
								/>
								<InputField
									cy={'member-title'}
									label={formatMessage({id: 'common.title'})}
									type="text"
									placeholder={formatMessage({id: 'first_login.enter_job_title'})}
									value={jobTitle}
									onChange={setJobTitle}
									onBlur={() => changeJobTitle(jobTitle)}
									blurOnEnter={true}
								/>
								{viewUser && !person.client ? <TeamMemberStatusSection person={person} /> : null}
								<div className="dates-container" data-userpilot="profile-dates">
									<DatePicker
										startDate={person.startDate ? moment(person.startDate) : null}
										label={formatMessage({id: 'common.start_date'})}
										handleDateRangeChange={value => updateStartDate(value)}
										clearBothDates={() => updateStartDate(null)}
										datePickerStyle={DATE_PICKER_STYLE.SLIM}
										isSingleDatePicker
										clearable
										endDateLimite={person.endDate ? moment(person.endDate).add(1, 'days') : null}
										hideLimitMessage
										disabled={!isAdmin}
									/>
									<div className="date-separator">-</div>
									<DatePicker
										startDate={person.endDate ? moment(person.endDate) : null}
										label={formatMessage({id: 'common.end_date'})}
										handleDateRangeChange={value => updateEndDate(value)}
										clearBothDates={() => updateEndDate(null)}
										datePickerStyle={DATE_PICKER_STYLE.SLIM}
										isSingleDatePicker
										clearable
										startDateLimite={person.startDate ? moment(person.startDate) : null}
										hideLimitMessage
										disabled={!isAdmin}
									/>
								</div>
							</div>
							<div className="user">
								<div className={props.isUploading ? 'upload-overlay' : 'upload-overlay-hidden'}>
									<div className={props.isUploading ? 'uploading-gif' : 'uploading-gif-hidden'} />
								</div>
								{person.profilePictureId ? (
									<img
										crossOrigin="use-credentials"
										data-private
										alt=""
										src={profilePicSrc(person.profilePictureId)}
										width="140"
										height="140"
									/>
								) : (
									<PersonInitials size={INITIALS_SIZE.EXTRA_LARGE} initials={person.initials} />
								)}

								<div className="controls">
									<div className="controls-update">
										<input
											id="file-input"
											type="file"
											onChange={handleChangeProfilePicture}
											ref={fileInputRef}
											onClick={e => {
												(e.target as HTMLInputElement).value = null as unknown as string;
											}}
										/>
										<label className="control update">
											<FormattedMessage id="settings_profile.update-photo" />
										</label>
									</div>
									<div className="control remove" onClick={deleteProfilePicture}>
										<FormattedMessage id="settings_profile.remove-photo" />
									</div>
								</div>
							</div>
						</fieldset>
						{person.client ? null : (
							<>
								<fieldset className="role-settings">
									<h3>
										<FormattedMessage id="settings_profile.role-labels-permissions" />{' '}
									</h3>
									<h4>
										<FormattedMessage id="common.role" />
									</h4>
									<p className="description">
										{viewUser ? (
											<FormattedMessage id="settings_profile.team-member-role-description" />
										) : (
											<FormattedMessage id="settings_profile.your-role-description" />
										)}
									</p>
									<SelectInput
										cy={'role-dropdown'}
										listDataCy={'role-option'}
										value={person.role ? person.role.id : null}
										options={allRoles}
										onChange={value => {
											changeRole(value ? value.value : null);
										}}
										placeholder={formatMessage({id: 'settings_profile.choose-role'})}
										label={formatMessage({id: 'common.role'})}
										multi={false}
										locked={!isAdmin}
									/>
									<div>
										<h4>
											<FormattedMessage id="common.labels" />
										</h4>
										<p className="description">
											{viewUser ? (
												<FormattedMessage id="settings_profile.select-labels-description-teammember" />
											) : (
												<FormattedMessage id="settings_profile.select-labels-description-viewer" />
											)}
										</p>
										<div className="skills-wrapper">
											<LabelDropdown
												labels={labels}
												selectedItems={selectedLabels}
												setSelectedItems={updateLabels}
												data-cy={'team-labels-dropdown'}
											/>
										</div>
									</div>
									{Util.hasDepartments()
										? [
												<h4>
													<FormattedMessage id="common.organization" />
												</h4>,
												<div className="organization-div">
													<SelectInput
														cy={'department-dropdown'}
														listDataCy={'department-option'}
														value={person.department ? person.department.id : null}
														options={departments}
														onChange={value => {
															changeDepartment(value ? value.value : null);
														}}
														placeholder={formatMessage({
															id: 'settings_profile.choose_department',
														})}
														label={formatMessage({id: 'common.department'})}
														multi={false}
														locked={!viewUser && !isAdmin}
													/>
													{person.costCenter ? (
														<InputField
															cy={'cost-center-title'}
															locked={true}
															label={formatMessage({id: 'common.cost_center'})}
															type="text"
															placeholder={formatMessage({id: 'common.no_cost_center'})}
															value={person.costCenter}
															autosize={true}
														/>
													) : (
														''
													)}
												</div>,
										  ]
										: ''}
								</fieldset>
							</>
						)}
						{person.client ? null : (
							<fieldset className="permissions">
								<h3>
									<FormattedMessage id="settings.permission_profile" />
								</h3>
								<div className="permissions-info-wrapper">
									<p className="description">
										{viewUser ? (
											<FormattedMessage id="settings_profile.permissions-desc-team" />
										) : (
											<FormattedMessage id="settings_profile.permissions-desc-viewer" />
										)}
									</p>
								</div>
								{props.showBillingInfo ? <FormattedMessage id="settings_profile.billing_info" /> : null}
								<SelectInput
									multi={hasModule(MODULE_TYPES.SISENSE)}
									showValuesInline={true}
									value={
										hasModule(MODULE_TYPES.SISENSE)
											? person.profiles.edges.map(e => {
													return {
														value: e.node.id,
														label: e.node.name,
													};
											  })
											: person.profiles.edges[0]?.node.id
									}
									options={getSelectableProfiles(viewer.company.profiles.edges)
										.filter(profile => viewUser || !personIsVirtualUser(profile))
										.map(profile => {
											return {
												label: profile.name,
												value: profile.id,
												profile: profile,
												disabled: (!viewUser && personIsAdmin(profile)) || isProfileUpdating,
											};
										})}
									onChange={value => {
										if (value.length) {
											changeProfile(value);
										}
									}}
									placeholder={getProfileName(
										viewer.company.profiles.edges.map(profile => profile.node),
										person.profiles.edges[0]?.node.id
									)}
									label={formatMessage({id: 'settings.permission_profile'})}
									locked={
										(!viewUser &&
											(!hasModule(MODULE_TYPES.SISENSE) ||
												!hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS))) ||
										((!person.invited || person.email === '') && personIsVirtualUser(person))
									}
								/>
							</fieldset>
						)}
						{Util.hasSkills() && (
							<fieldset className="skills">
								<ProfileSkills person={person} company={company} />
							</fieldset>
						)}
						{person.client ? null : (
							<fieldset className="working-hours">
								<h3>
									<FormattedMessage id="common.working_hours" />
								</h3>
								<div>
									<table className="working-hours-table">
										<thead>
											<tr>
												{weekdays.map((day, index) => (
													<th key={index}>{day}</th>
												))}
												<th>&nbsp;</th>
											</tr>
										</thead>

										<tbody>
											<tr>
												{[...Array(7)].map((_, i) => (
													<td key={i} className="input-cell">
														<HoursInput
															value={workingHours[i]}
															nonWorkingDay={workingHours[i] === 0}
															mutation={e => saveWorkingHours(i, e)}
															disabled={Util.isWeekdayDisabled(i, company, viewer.language)}
														/>
													</td>
												))}
											</tr>
										</tbody>
									</table>
								</div>
								<SelectInput
									cy={'holiday-calendar-input'}
									listDataCy={'holiday-option'}
									placeholder={formatMessage({id: 'settings_holiday_calendars.select_holiday_calendar'})}
									options={holidayCalendarOptions}
									onChange={e => {
										changeHolidayCalendar(e ? e.value : null);
									}}
									value={person.holidayCalendar ? person.holidayCalendar.id : null}
									clearable={false}
									label={formatMessage({id: 'settings_holiday_calendars.holiday_calendar'})}
									multi={false}
								/>
							</fieldset>
						)}
						<fieldset className="locale-settings">
							<h3>
								<FormattedMessage id="settings_profile.language-dates-numbers" />
							</h3>
							<p className="description">
								{viewUser
									? person.client
										? formatMessage({id: 'settings_profile.language_description_client_user'})
										: formatMessage({id: 'settings_profile.language-description-team-member'})
									: formatMessage({id: 'settings_profile.language-description'})}
							</p>
							<SelectInput
								listDataCy={'localization-option'}
								cy={'localization-preferences'}
								placeholder={formatMessage({id: 'settings_profile.choose-language'})}
								options={languages}
								onChange={value => {
									changeLanguage(value ? value.value : null);
								}}
								value={person.language}
								clearable={false}
								label={formatMessage({id: 'settings.profile-language'})}
								multi={false}
							/>
						</fieldset>
						{hasModule(MODULE_TYPES.SISENSE) && (
							<fieldset>
								<h3>{'Advanced Analytics'}</h3>
								<p className="description">
									{`${viewUser ? 'This person is' : 'You are'} ${
										person.sisenseUserId ? '' : 'not'
									} linked to an Advanced Analytics user.`}
								</p>
								{person.sisenseUserId && (
									<Button
										onClick={deleteSisenseUser}
										size={Button.SIZE.STANDARD}
										variant={Button.VARIANT.RED_FILLED}
									>
										Delete User
									</Button>
								)}
							</fieldset>
						)}
						{harvestLoading ? (
							<fieldset className="harvest-settings">
								<div className="harvest-title">
									<img width={30} height={30} src={HarvestIcon} alt="Harvest" />
									<h3 className="harvest-header">
										<FormattedMessage id="title.app_harvest" />
									</h3>
								</div>

								<h4>
									<FormattedMessage id="common.loading" />
								</h4>
							</fieldset>
						) : company.harvestEnabled ? (
							<fieldset className="harvest-settings">
								<div className="harvest-title">
									<img width={30} height={30} src={HarvestIcon} alt="Harvest" />
									<h3 className="harvest-header">
										<FormattedMessage id="title.app_harvest" />
									</h3>
								</div>

								<div className={'harvest-description'}>
									<FormattedMessage id="settings.linked_harvest_user" />
								</div>
								<div>{harvestUsersName}</div>
							</fieldset>
						) : null}
						{!person.client &&
						(person.calendarKey !== null || company.calendarApiEnabled || company.outlookCalendarApiEnabled) ? (
							<fieldset className="calendar-integration-settings">
								<h3>Calendar Integration</h3>
								{person.calendarKey !== null ? (
									<>
										<h4>Sync task deadlines to your own calendar</h4>
										<div className="description">
											Stay in the loop with your ongoing task deadlines by syncing it to your calendar of
											choice: G Cal, Outlook Cal and Apple Calendar.
										</div>
										<div className="description">
											{viewUser
												? 'This is the persons private url.'
												: 'This is your private calendar link, paste it into your calendar tool to enable your events.'}
										</div>
										<div className="input-wrapper">
											<InputField
												label={formatMessage({id: 'common.link'})}
												value={'https://api.forecast.it/calendar/' + person.calendarKey}
												disabled={true}
												type="text"
												placeholder=""
											/>
											<ClipboardButton
												className="copy-button"
												title={formatMessage({id: 'common.copy'})}
												data-clipboard-text={'https://api.forecast.it/calendar/' + person.calendarKey}
											/>
										</div>
									</>
								) : null}
								{company.calendarApiEnabled && !viewUser ? (
									<>
										<div className={'section-border'} />
										<SettingsCalendarWrapper
											viewer={viewer}
											personId={person.actualPersonId ?? person.id}
											type="google"
										/>
									</>
								) : null}
								{company.outlookCalendarApiEnabled && !viewUser ? (
									<>
										<div className={'section-border'} />
										<SettingsCalendarWrapper
											viewer={viewer}
											personId={person.actualPersonId ?? person.id}
											type="outlook"
										/>
									</>
								) : null}
							</fieldset>
						) : null}
						{
							// GitLab
							isAdmin && company.gitlabEnabled ? (
								<fieldset className="gitlab-integration-settings">
									<h3>GitLab.com</h3>
									<p className="description">
										<FormattedMessage id="settings_profile.gitlab_description" />
									</p>
									{person.gitlabUser && person.gitlabUser.integrationUserId ? (
										linkedGitlabUser ? (
											<>
												<div className="gitlab-user">
													<img height="42" width="42" src={linkedGitlabUser.avatar_url} />
													<div className="name">
														<b>{linkedGitlabUser.name}</b>
														<br />
														{'@' + linkedGitlabUser.username}
													</div>
												</div>
												<UnlinkButton
													onClick={() => removeGitLabUser(false)}
													messageId="settings_profile.gitlab_unlink"
												/>
											</>
										) : null
									) : (
										<div>
											<div className="input-wrapper">
												<InputField
													label={formatMessage({id: 'settings_profile.gitlab_username'})}
													type="text"
													placeholder={formatMessage({id: 'settings_profile.gitlab_username'})}
													onChange={searchString => {
														setGitlabSearchString(searchString || '');
													}}
													onEnter={() => fetchGitLabUsers(false)}
												/>
												<button onClick={() => fetchGitLabUsers(false)}>
													<FormattedMessage id="common.search" />
												</button>
											</div>
											{gitlabLoading ? (
												<div>
													<FormattedMessage id="common.loading" />
												</div>
											) : (
												<div className="users">
													{gitlabUsers.map(u => (
														<div className="gitlab-user" onClick={() => selectGitlabUser(u, false)}>
															<img height="42" width="42" src={u.avatar_url} />
															<div className="name">
																<b>{u.name}</b>
																<br />
																{'@' + u.username}
															</div>
														</div>
													))}
												</div>
											)}
										</div>
									)}
								</fieldset>
							) : null
						}
						{
							// GitLab Server
							isAdmin && company.gitlabServerEnabled ? (
								<fieldset className="gitlab-integration-settings">
									<h3>GitLab Server</h3>
									<p className="description">
										<FormattedMessage id="settings_profile.gitlab_description" />
									</p>
									{person.gitlabServerUser && person.gitlabServerUser.integrationUserId ? (
										linkedGitlabServerUser ? (
											<>
												<div className="gitlab-user">
													<img height="42" width="42" src={linkedGitlabServerUser.avatar_url} />
													<div className="name">
														<b>{linkedGitlabServerUser.name}</b>
														<br />
														{'@' + linkedGitlabServerUser.username}
													</div>
												</div>
												<UnlinkButton
													onClick={() => removeGitLabUser(true)}
													messageId="settings_profile.gitlab_unlink"
												/>
											</>
										) : null
									) : (
										<div>
											<div className="input-wrapper">
												<InputField
													label={formatMessage({id: 'settings_profile.gitlab_username'})}
													type="text"
													placeholder={formatMessage({id: 'settings_profile.gitlab_username'})}
													onChange={searchString => {
														setGitlabServerSearchString(searchString || '');
													}}
													onEnter={() => fetchGitLabUsers(true)}
												/>
												<button onClick={() => fetchGitLabUsers(true)}>
													<FormattedMessage id="common.search" />
												</button>
											</div>
											{gitlabServerLoading ? (
												<div>
													<FormattedMessage id="common.loading" />
												</div>
											) : (
												<div className="users">
													{gitlabServerUsers.map(u => (
														<div className="gitlab-user" onClick={() => selectGitlabUser(u, true)}>
															<img height="42" width="42" src={u.avatar_url} alt="" />
															<div className="name">
																<b>{u.name}</b>
																<br />
																{'@' + u.username}
															</div>
														</div>
													))}
												</div>
											)}
										</div>
									)}
								</fieldset>
							) : null
						}
						{
							// GitHub
							!person.client && (isAdmin || !viewUser) && company.githubEnabled ? (
								<fieldset className="github-integration-settings">
									<h3>GitHub</h3>
									<p className="description">
										<FormattedMessage id="settings_profile.github_description" />
									</p>
									{person.githubUser && person.githubUser.integrationUserId ? (
										linkedGithubUser ? (
											<>
												<div className="github-user">
													<img height="42" width="42" src={linkedGithubUser.avatar_url} />
													<div className="name">
														<b>{linkedGithubUser.username}</b>
														<br />
														{'@' + linkedGithubUser.username}
													</div>
												</div>
												<UnlinkButton
													onClick={removeGithubUser}
													messageId="settings_profile.github_unlink"
												/>
											</>
										) : null
									) : (
										<div>
											<div className="input-wrapper">
												<InputField
													label={formatMessage({id: 'settings_profile.github_username'})}
													type="text"
													placeholder={formatMessage({id: 'settings_profile.github_username'})}
													onChange={searchString => {
														setGithubSearchString(searchString || '');
													}}
													onEnter={fetchGithubUsers}
												/>
												<button onClick={fetchGithubUsers}>
													<FormattedMessage id="common.search" />
												</button>
											</div>
											{githubLoading ? (
												<div>
													<FormattedMessage id="common.loading" />
												</div>
											) : (
												<div className="users">
													{githubUsers.map(u => (
														<div className="github-user" onClick={() => selectGithubUser(u)}>
															<img height="42" width="42" src={u.avatar_url} alt={''} />
															<div className="name">
																<b>{u.username}</b>
																<br />
																{'@' + u.username}
															</div>
														</div>
													))}
												</div>
											)}
										</div>
									)}
								</fieldset>
							) : null
						}
						{
							// Jira
							!person.client && (isAdmin || !viewUser) && company.jiraCloudEnabled ? (
								<fieldset className="common-integration-settings">
									<h3>Jira Cloud</h3>
									<p className="description">
										{person.jiraCloudId ? (
											jiraCloudUsers ? (
												<>
													<FormattedMessage
														id={'settings_profile.jira_linked_user'}
														values={{
															jiraUser: jiraCloudUsername,
														}}
													/>
													<UnlinkButton onClick={() => unlinkJiraUser('cloud')} />
												</>
											) : null
										) : (
											<>
												<FormattedMessage id="settings_profile.jira_description" />
												<div className="common-integration-user-dropdown">
													<Dropdown
														value={null}
														options={jiraCloudUsers.filter(u => !u.linked)}
														onChange={val => linkJiraUser(val, 'cloud')}
														hideLabel={true}
														placeholder={'Type to search'}
														customHeight={30}
														integrationDropdown={true}
														integrationLoading={jiraCloudLoading}
														onSearchValueChange={searchString => fetchJiraCloudUsers(searchString)}
														emptyMessage="Start typing to search for a Jira Cloud user"
													/>
												</div>
											</>
										)}
									</p>
								</fieldset>
							) : null
						}
						{
							// Jira Server
							!person.client && (isAdmin || !viewUser) && company.jiraServerEnabled ? (
								<fieldset className="common-integration-settings">
									<h3>Jira Server</h3>
									<p className="description">
										{person.jiraServerId ? (
											jiraServerUsers ? (
												<>
													<FormattedMessage
														id={'settings_profile.jira_linked_user'}
														values={{
															jiraUser: jiraServerUsername,
														}}
													/>
													<UnlinkButton onClick={() => unlinkJiraUser('server')} />
												</>
											) : null
										) : (
											<>
												<FormattedMessage id="settings_profile.jira_description" />
												<div className="common-integration-user-dropdown">
													<Dropdown
														value={null}
														options={jiraServerUsers.filter(u => !u.linked)}
														onChange={val => linkJiraUser(val, 'server')}
														hideLabel={true}
														placeholder={formatMessage({id: 'integrations.jira_user'})}
														customHeight={30}
														integrationDropdown={true}
														integrationLoading={jiraServerLoading}
														integrationRetryToFetch={fetchJiraServerUsers}
													/>
												</div>
											</>
										)}
									</p>
								</fieldset>
							) : null
						}
						{
							// Unit4 - Show for admin or when viewing your own profile
							!person.client && (isAdmin || !viewUser) && company.unit4Enabled ? (
								<fieldset className="common-integration-settings">
									<h3>Unit4</h3>
									<p className="description">
										{person.unit4UserObject ? (
											unit4Users ? (
												<>
													<FormattedMessage
														id="settings_profile.unit4_linked_user"
														values={{
															unit4User: unit4Username,
														}}
													/>
													<UnlinkButton onClick={unlinkUnit4User} />
												</>
											) : null
										) : (
											<>
												<FormattedMessage id="settings_profile.unit4_description" />
												<div className="common-integration-user-dropdown">
													<Dropdown
														value={null}
														options={unit4UserOptions}
														onChange={val => linkUnit4User(val)}
														hideLabel={true}
														placeholder={formatMessage({
															id: 'integrations.unit4_user.placeholder',
														})}
														customHeight={30}
														integrationDropdown={true}
														integrationLoading={unit4Loading}
														onSearchValueChange={fetchUnit4Resources}
													/>
												</div>
											</>
										)}
									</p>
								</fieldset>
							) : null
						}
						{
							// BambooHR - Show for admin or when viewing your own profile
							!person.client && (isAdmin || !viewUser) && company.bambooHREnabled ? (
								<fieldset className="common-integration-settings">
									<h3>BambooHR</h3>
									<p className="description">
										{person.bambooHRId ? (
											bambooHRUsers ? (
												<>
													<FormattedMessage
														id="settings_profile.bamboohr_linked_user"
														values={{
															bamboohrUser: bambooHRUsername,
														}}
													/>
													<div>
														<UnlinkButton onClick={() => setBambooHRUser()} />
													</div>
												</>
											) : null
										) : (
											<>
												<FormattedMessage id="settings_profile.bamboohr_description" />
												<div className="common-integration-user-dropdown">
													<Dropdown
														value={null}
														options={bambooHRUsers}
														onChange={val => setBambooHRUser(val)}
														hideLabel={true}
														placeholder={formatMessage({id: 'integrations.bamboohr_user'})}
														customHeight={30}
														integrationDropdown={true}
														integrationLoading={bambooHRLoading}
														integrationRetryToFetch={fetchBambooHRUsers}
													/>
												</div>
											</>
										)}
									</p>
								</fieldset>
							) : null
						}
						{
							// Sage Intacct - Show for non-client, admin, or when viewing your own profile
							!person.client &&
							(isAdmin || !viewUser) &&
							hasFeatureFlag('sage_intacct_integration') &&
							company.sageIntacctConnectionEnabled ? (
								<fieldset className="common-integration-settings">
									<h3>Sage Intacct</h3>
									<p className="description">
										{person.sageIntacctId ? (
											<>
												{`${person.firstName} is linked to the following Sage Intacct profile: ${
													person.sageIntacctName
												} ${person?.sageIntacctEmail ? `(${person.sageIntacctEmail})` : ''}`}
												<div>
													<UnlinkButton onClick={() => setSageIntacctUser()} />
												</div>
											</>
										) : (
											<>
												{`Select a profile
                            from the list below to associate this team member
                            with a Sage Intacct profile.`}
												<div className="common-integration-user-dropdown">
													<Dropdown
														value={null}
														options={sageIntacctUsers}
														onChange={val => setSageIntacctUser(val)}
														hideLabel={true}
														placeholder={`Select Sage Intacct profile`}
														customHeight={30}
														integrationDropdown={true}
														integrationLoading={sageIntacctLoading}
														integrationRetryToFetch={fetchSageIntacctUsers}
													/>
												</div>
											</>
										)}
									</p>
								</fieldset>
							) : null
						}
						{hasFeatureFlag('hibob_integration') && (
							<HiBobIntegration person={person} viewUser={viewUser} company={company} />
						)}
						{
							// Slack - Show when viewing your own profile
							!person.client && company.newSlackEnabled ? (
								<fieldset className="common-integration-settings">
									<h3>Slack</h3>
									<p className="description">
										{person.slackId ? (
											slackUsers ? (
												<>
													<FormattedMessage
														id="settings_profile.slack_linked_user"
														values={{
															slackUser: slackUsername,
														}}
													/>
													<div>
														<UnlinkButton onClick={() => unlinkSlackUser()} />
													</div>
												</>
											) : null
										) : (
											<>
												<FormattedMessage id="settings_profile.slack_description" />
												<div>
													<Button
														onClick={() => linkSlackUser()}
														size={Button.SIZE.STANDARD}
														variant={Button.VARIANT.GREEN_FILLED}
													>
														{formatMessage({id: 'common.link'})}
													</Button>
												</div>
											</>
										)}
									</p>
								</fieldset>
							) : null
						}
						{
							// Azure DevOps - Show for non-client, admin, or when viewing your own profile
							!person.client && (isAdmin || !viewUser) && company.vstsEnabled ? (
								<fieldset className="common-integration-settings">
									<h3>Azure DevOps</h3>
									<p className="description">
										{person.adoUserId ? (
											<>
												{`${person.firstName} is linked to an Azure DevOps team member`}
												<div>
													<UnlinkButton onClick={() => unlinkAdoUser()} />
												</div>
											</>
										) : (
											<>
												{`Link this user to an Azure DevOps team member`}
												<div>
													<Button
														onClick={() => linkAdoUser()}
														size={Button.SIZE.STANDARD}
														variant={Button.VARIANT.GREEN_FILLED}
													>
														{formatMessage({id: 'common.link'})}
													</Button>
												</div>
											</>
										)}
									</p>
								</fieldset>
							) : null
						}
						{forecast_user === 'true' && (
							<div title={'web fron version'} className={'web-fron-version'}>
								version {webFrontVersion} {webFrontVersionTime.format('DD/MM/YY HH:mm')}
							</div>
						)}
					</div>
				</div>
			</CustomScrollDiv>
		</div>
	);
};
